/**
 * Interface Project Panel Component
 * Interface properties management (name, description, settings)
 */

import { useState, useEffect } from 'react';
import { useBootstrapTheme } from '../../contexts/BootstrapThemeContext';
import { useSearchParams } from 'react-router-dom';

interface InterfaceProjectPanelProps {
  className?: string;
  onInterfaceUpdate?: (name: string, description?: string) => void;
}

export function InterfaceProjectPanel({ className = '', onInterfaceUpdate }: InterfaceProjectPanelProps) {
  const { effectiveTheme } = useBootstrapTheme();
  const [searchParams] = useSearchParams();
  
  const [interfaceName, setInterfaceName] = useState('');
  const [interfaceDescription, setInterfaceDescription] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Get workflow ID from URL params
  const workflowId = searchParams.get('workflow');

  // Initialize interface properties
  useEffect(() => {
    const loadInterfaceData = () => {
      if (workflowId) {
        // Load from workflow-specific interface settings if available
        const savedName = sessionStorage.getItem(`interface-name-${workflowId}`);
        const savedDescription = sessionStorage.getItem(`interface-description-${workflowId}`);
        
        setInterfaceName(savedName || `Interface for Workflow ${workflowId}`);
        setInterfaceDescription(savedDescription || 'User interface for workflow interaction');
      } else {
        // New standalone interface
        setInterfaceName(`Interface ${new Date().toLocaleDateString()}`);
        setInterfaceDescription('Standalone user interface');
      }
      setIsLoading(false);
    };

    loadInterfaceData();
  }, [workflowId]);

  // Update parent component when values change
  useEffect(() => {
    if (onInterfaceUpdate && interfaceName.trim()) {
      onInterfaceUpdate(interfaceName.trim(), interfaceDescription.trim());
    }
    
    // Save to session storage
    if (workflowId) {
      sessionStorage.setItem(`interface-name-${workflowId}`, interfaceName);
      sessionStorage.setItem(`interface-description-${workflowId}`, interfaceDescription);
    }
  }, [interfaceName, interfaceDescription, onInterfaceUpdate, workflowId]);

  if (isLoading) {
    return (
      <div className={`d-flex justify-content-center align-items-center py-5 ${className}`}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-3 ${className}`}>
      <div className="mb-4">
        <h6 className="text-muted mb-3 d-flex align-items-center">
          <span className="me-2">🎨</span>
          Interface Properties
        </h6>
        
        {/* Interface Name */}
        <div className="mb-3">
          <label htmlFor="interface-name" className="form-label fw-medium">
            <span className="me-1">📝</span>
            Interface Name
          </label>
          <input
            type="text"
            id="interface-name"
            className="form-control"
            style={{
              backgroundColor: effectiveTheme === 'dark' ? '#212529' : '#ffffff',
              color: effectiveTheme === 'dark' ? '#ffffff' : '#000000',
              borderColor: effectiveTheme === 'dark' ? '#6c757d' : '#ced4da'
            }}
            value={interfaceName}
            onChange={(e) => setInterfaceName(e.target.value)}
            placeholder="Enter interface name..."
            maxLength={100}
          />
          <div className="form-text">
            <small className="text-muted">
              {interfaceName.length}/100 characters
            </small>
          </div>
        </div>

        {/* Interface Description */}
        <div className="mb-3">
          <label htmlFor="interface-description" className="form-label fw-medium">
            <span className="me-1">📄</span>
            Description
            <span className="text-muted fw-normal"> (optional)</span>
          </label>
          <textarea
            id="interface-description"
            className="form-control"
            style={{
              backgroundColor: effectiveTheme === 'dark' ? '#212529' : '#ffffff',
              color: effectiveTheme === 'dark' ? '#ffffff' : '#000000',
              borderColor: effectiveTheme === 'dark' ? '#6c757d' : '#ced4da'
            }}
            rows={3}
            value={interfaceDescription}
            onChange={(e) => setInterfaceDescription(e.target.value)}
            placeholder="Describe what this interface does..."
            maxLength={500}
          />
          <div className="form-text">
            <small className="text-muted">
              {interfaceDescription.length}/500 characters
            </small>
          </div>
        </div>
      </div>

      {/* Interface Type */}
      <div className="mb-4">
        <h6 className="text-muted mb-3 d-flex align-items-center">
          <span className="me-2">🏷️</span>
          Interface Type
        </h6>
        
        <div 
          className="card border-0" 
          style={{
            backgroundColor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa'
          }}
        >
          <div className="card-body p-3">
            <div className="row g-3 text-center">
              <div className="col-6">
                <div className="fw-bold text-info">
                  {workflowId ? 'Workflow' : 'Standalone'}
                </div>
                <small className="text-muted">Type</small>
              </div>
              <div className="col-6">
                <div className="fw-bold text-success">
                  Interactive
                </div>
                <small className="text-muted">Mode</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Connection Info */}
      {workflowId && (
        <div className="mb-4">
          <h6 className="text-muted mb-3 d-flex align-items-center">
            <span className="me-2">🔗</span>
            Workflow Connection
          </h6>
          
          <div 
            className="card border-0" 
            style={{
              backgroundColor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(25, 135, 84, 0.05)'
            }}
          >
            <div className="card-body p-3">
              <div className="d-flex align-items-center">
                <span className="me-2">✅</span>
                <div>
                  <div className="fw-medium small">Connected to Workflow</div>
                  <div className="text-muted small">ID: {workflowId}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="card border-0" style={{
        backgroundColor: effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(13, 110, 253, 0.05)'
      }}>
        <div className="card-body p-3">
          <h6 className="card-title mb-2 d-flex align-items-center">
            <span className="me-2">💡</span>
            Interface Tips
          </h6>
          <ul className="list-unstyled mb-0 small">
            <li className="mb-1">
              <span className="text-muted">•</span> Drag components from the library to the canvas
            </li>
            <li className="mb-1">
              <span className="text-muted">•</span> Use the properties panel to customize components
            </li>
            <li className="mb-0">
              <span className="text-muted">•</span> Preview your interface with the Preview tab
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}